package com.shadowings.gero.presentation.component.avatars

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.screen.characters.CharacterConsts
import com.shadowings.gero.presentation.screen.characters.backgroundOptions
import com.shadowings.gero.presentation.screen.characters.eyesOptions
import com.shadowings.gero.presentation.screen.characters.hairOptions
import com.shadowings.gero.presentation.screen.characters.shirtOptions
import com.shadowings.gero.presentation.screen.characters.skinOptions
import com.shadowings.gero.presentation.ui.AppTheme
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.masko_0_collo
import gero.composeapp.generated.resources.masko_1_orecchie_back
import gero.composeapp.generated.resources.masko_2_orecchie_front
import gero.composeapp.generated.resources.masko_3_corpo
import gero.composeapp.generated.resources.masko_4_antenne
import gero.composeapp.generated.resources.masko_5_occhi
import gero.composeapp.generated.resources.masko_6_occhiali
import gero.composeapp.generated.resources.masko_7_baffi
import gero.composeapp.generated.resources.masko_8_naso
import gero.composeapp.generated.resources.masko_9_sopracciglia
import gero.composeapp.generated.resources.masko_einstein_baffi
import gero.composeapp.generated.resources.masko_einstein_capelli
import gero.composeapp.generated.resources.masko_einstein_occhiali
import gero.composeapp.generated.resources.masko_layer_sad
import gero.composeapp.generated.resources.masko_layer_surprised
import gero.composeapp.generated.resources.masko_pirate_hat
import gero.composeapp.generated.resources.masko_pirate_occhiali
import gero.composeapp.generated.resources.masko_rich_hat
import gero.composeapp.generated.resources.masko_rich_occhiali
import org.jetbrains.compose.resources.DrawableResource

@Preview
@Composable
fun MaskoAvatarPreview() {
    AppTheme {
        FlowRow {
            Faces.entries.forEach { face ->
                Column {
                    MaskoAvatar(
                        face = face,
                        withBackground = true
                    )
                }
            }
        }
    }
}

@Composable
fun MaskoAvatar(
    userData: UserData = UserData(),
    face: Faces = Faces.Idle,
    modifier: Modifier = Modifier.size(256.dp),
    withBackground: Boolean = true
) {

    val bgColorIndex =
        (userData.maskoColors[CharacterConsts.BG_KEY] ?: 9).coerceIn(0, backgroundOptions.size - 1)
    val skinColorIndex = (userData.maskoColors["skin"] ?: 0).coerceIn(0, shirtOptions.size - 1)
    val glassesIndex = (userData.maskoColors["glasses"] ?: 9).coerceIn(0, hairOptions.size - 1)
    val shirtColorIndex = (userData.maskoColors["shirt"] ?: 7).coerceIn(0, shirtOptions.size - 1)
    val baffiIndex = (userData.maskoColors["baffi"] ?: 9).coerceIn(0, hairOptions.size - 1)
    val eyebrowsIndex = (userData.maskoColors["eyebrows"] ?: 9).coerceIn(0, hairOptions.size - 1)
    val eyesColorIndex = (userData.maskoColors["eyes"] ?: 1).coerceIn(0, eyesOptions.size - 1)
    val nasoColorIndex = (userData.maskoColors["naso"] ?: 0).coerceIn(0, skinOptions.size - 1)
    val maskTypeIndex = (userData.maskoColors[CharacterConsts.MASK_TYPE_KEY] ?: 0).coerceIn(0, 3)
    val maskAccentColorIndex =
        (userData.maskoColors[CharacterConsts.MASK_ACCENT_KEY] ?: 9).coerceIn(
            0,
            hairOptions.size - 1
        )

    val backLayer: List<Pair<DrawableResource, Color>> = when (maskTypeIndex) {
        3 -> listOf(
            Res.drawable.masko_einstein_capelli to hairOptions[baffiIndex]
        )

        else -> listOf()
    }

    // index 0 = base (mustache+glasses+eyebrows, today's default), 1 = pirata, 2 = riccone,
    // 3 = einstein — none of the themed masks have an eyebrows asset, so eyebrows are hidden
    // whenever a mask other than base is active.
    val maskLayerPairs: List<Pair<DrawableResource, Color>> = when (maskTypeIndex) {
        1 -> listOf(
            Res.drawable.masko_pirate_occhiali to hairOptions[glassesIndex],
            Res.drawable.masko_7_baffi to hairOptions[baffiIndex],
            Res.drawable.masko_pirate_hat to hairOptions[maskAccentColorIndex],
        )

        2 -> listOf(
            Res.drawable.masko_rich_occhiali to hairOptions[glassesIndex],
            Res.drawable.masko_7_baffi to hairOptions[baffiIndex],
            Res.drawable.masko_rich_hat to hairOptions[maskAccentColorIndex],
        )

        3 -> listOf(
            Res.drawable.masko_einstein_occhiali to hairOptions[glassesIndex],
            Res.drawable.masko_einstein_baffi to hairOptions[baffiIndex]
        )

        else -> listOf(
            Res.drawable.masko_7_baffi to hairOptions[baffiIndex],
            Res.drawable.masko_6_occhiali to hairOptions[glassesIndex],
            Res.drawable.masko_9_sopracciglia to hairOptions[eyebrowsIndex],
        )
    }

    val pairs: List<Pair<DrawableResource, Color>> = backLayer + listOf(
        Res.drawable.masko_0_collo to shirtOptions[shirtColorIndex],
        Res.drawable.masko_1_orecchie_back to shirtOptions[skinColorIndex],
        Res.drawable.masko_2_orecchie_front to shirtOptions[skinColorIndex].darken(),
        Res.drawable.masko_4_antenne to shirtOptions[skinColorIndex],
        Res.drawable.masko_3_corpo to shirtOptions[skinColorIndex],
        Res.drawable.masko_5_occhi to eyesOptions[eyesColorIndex],
    ) + maskLayerPairs + listOf(
        Res.drawable.masko_8_naso to skinOptions[nasoColorIndex].reddish()
    ) + when (face) {
        Faces.Surprised -> listOf(Res.drawable.masko_layer_surprised to Color(0xFFBE2E2E))
        Faces.Sad -> listOf(Res.drawable.masko_layer_sad to Color(0xFFBE2E2E))
        else -> listOf()
    }

    val expression = emptyList<Pair<DrawableResource, Color>>()

    BuildAvatar(withBackground, bgColorIndex, pairs, expression, modifier, face)
}
